import { MAX_HEIGHT, MIN_Y } from "./constants.js";
import { applyFeature, featureKind } from "./features/index.js";
import { createNoise } from "./noise.js";

function clampHeight(height) {
    return Math.max(MIN_Y, Math.min(MAX_HEIGHT, Math.round(height)));
}

// Each feature is stamped from the same input. Where stamps overlap, `pick` keeps one column.
function applyOverlapping(heights, resolution, size, features, pick) {
    const count = features.length;
    if (count === 0) return;
    if (count === 1) {
        applyFeature(heights, resolution, size, features[0]);
        return;
    }

    const input = new Int16Array(heights);
    const layer = new Int16Array(heights.length);
    const written = new Uint8Array(heights.length);
    const writtenList = [];
    const covered = new Uint8Array(heights.length);
    layer.written = written;
    layer.writtenList = writtenList;

    for (let n = 0; n < count; n++) {
        layer.set(input);
        for (let k = 0; k < writtenList.length; k++) written[writtenList[k]] = 0;
        writtenList.length = 0;

        applyFeature(layer, resolution, size, features[n]);

        for (let k = 0; k < writtenList.length; k++) {
            const index = writtenList[k];
            if (covered[index] === 0) {
                heights[index] = layer[index];
                covered[index] = 1;
            } else {
                heights[index] = pick(heights[index], layer[index]);
            }
        }
    }
}

export function generateHeights(layout, resolution = layout.size) {
    const size = layout.size;
    const stride = size / resolution;
    const base = layout.base;
    const noise = createNoise(layout.seed >>> 0);
    const heights = new Int16Array(resolution * resolution);

    for (let j = 0; j < resolution; j++) {
        const z = (j + 0.5) * stride;
        const row = j * resolution;
        for (let i = 0; i < resolution; i++) {
            const x = (i + 0.5) * stride;
            const n = noise.fbm(x * base.scale, z * base.scale, base.octaves);
            heights[row + i] = clampHeight(base.height + n * base.amplitude);
        }
    }

    const constructive = [];
    const destructive = [];
    const features = layout.features ?? [];
    for (let n = 0; n < features.length; n++) {
        const feature = features[n];
        if (featureKind(feature.type) === "destructive") destructive.push(feature);
        else constructive.push(feature);
    }

    applyOverlapping(heights, resolution, size, constructive, Math.max);
    applyOverlapping(heights, resolution, size, destructive, Math.min);

    return {
        heights,
        baseHeights: new Int16Array(heights),
        resolution,
    };
}
